import cn from 'classnames';
import moment, { Moment } from 'moment';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  ButtonUI,
  showNotification,
  Tag,
  Tooltip,
  TooltipInteractionEnum,
  useQueryFetch
} from 'sonata-react-components';
import {
  getCampaignPlan,
  getInsertionOrder,
  getInsertionOrderState,
  getLineItemStatus
} from 'sonata-static-storage/lib/entities';

import { checkLocationsOrScreensAreConfigured } from '@/components/common/Sidebar/mainSection.helper';
import { countAdChanges } from '@/helpers/ads/adSaveActions.helper';
import {
  CONTEXT_TARGETING_FIELDS,
  CREATIVES_OTHER_FIELDS,
  countChangedFields
} from '@/helpers/entity/entityFieldChanges.helper';
import { IoSettingValidationError, isValidIoSettings } from '@/helpers/ioSettings/ioSettings.helper.ts';
import { BIDDER_SYNC_ERROR_MESSAGE, getSavedEntitySyncErrorMessage } from '@/helpers/syncError/syncError.helper.ts';
import { useGlobalStore } from '@/store/global';
import { getSessionByKeys } from '@/store/global/session/selectors.ts';
import {
  getHasUnsavedChanges as getHasUnsavedIOChanges,
  getHasUnsavedDoohAudienceChanges as getHasUnsavedIODoohAudienceChanges,
  getHasUnsavedEntityChanges as getHasUnsavedIOEntityChanges,
  MAIN_SETTINGS_FIELDS as IO_MAIN_SETTINGS_FIELDS,
  useStore as useInsertionOrderStore
} from '@/store/local/ioSettings';
import {
  getHasUnsavedChanges as getHasUnsavedLIChanges,
  getHasUnsavedDoohAudienceChanges as getHasUnsavedLIDoohAudienceChanges,
  getHasUnsavedEntityChanges as getHasUnsavedLIEntityChanges,
  MAIN_SETTINGS_FIELDS as LI_MAIN_SETTINGS_FIELDS,
  useStore as useLineItemStore
} from '@/store/local/lineItem';

import type { MessageDescriptor } from 'react-intl';
import type {
  Layer,
  LineItemPacingProgressTotal,
  MultiFlightEntityState,
  MultiFlightEntityType
} from 'sonata-static-storage/lib/common/common.types';
import type { InsertionOrderFlight } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrderFlight.schema';
import type { IoSettingsStore } from '@/store/local/ioSettings';
import type { LineItemStore } from '@/store/local/lineItem';
import type { SaveSteps } from '@/types/saveSteps.types';

import './subHeader.styles.scss';
import { useEffect, useRef, useState } from 'react';
import { ContextualDOOHHeaderMenu } from 'sonata-platform-components';
import { getLineItemPacing } from 'sonata-static-storage/lib/entities/lineItem/lineItem.service';
import { normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import { GoalTimeFrameEnum, GoalTypeEnum } from 'sonata-static-storage/lib/schemas/campaign/goal.schema';
import { GeoDataAndScreensInteface } from 'sonata-static-storage/lib/schemas/screen/screen.schema';

import { useInsertionOrderFormContext } from '@/components/ioSettings/InsertionOrderForm/InsertionOrderForm.provider';
import { useLineItemFormContext } from '@/components/liSettings/LineItemForm/LineItemForm.provider';
import { useNavigationGuard } from '@/context/NavigationGuardProvider';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import SaveProgressTooltipContent from './SaveProgressTooltipContent';

import type { ReactNode } from 'react';

type Props = {
  budget: number;
  completed: boolean;
  dates?: { end?: string; start?: string };
  entity: MultiFlightEntityType;
  hasUnsavedEntityChanges: boolean;
  isSaveDisabled: boolean;
  isSnliActive: boolean;
  name: string;
  onExitSnli: () => void;
  onExport: (response: Record<string, any>) => void;
  onHandleButtonClick: (save?: boolean) => void;
  onProgressTooltipOpenChange?: (isOpen: boolean) => void;
  parentName: string;
  saveSteps: SaveSteps;
  screenGroupsLayer?: Layer | null;
  screens?: GeoDataAndScreensInteface[];
  status?: MultiFlightEntityState;
  state: Record<string, any>;
  type: CampaignFamilyEnum | undefined;
};

function SaveProgressTooltip({
  children,
  content,
  enabled,
  onOpenChange
}: {
  children: ReactNode;
  content: ReactNode;
  enabled: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}) {
  return (
    <Tooltip
      className="subHeader__progressTooltip"
      config={{
        component: content,
        interactions: enabled ? [TooltipInteractionEnum.hover] : [],
        offset: 8,
        onOpen: onOpenChange,
        placement: 'bottom-end',
        strategy: 'absolute'
      }}
    >
      <span className="subHeader__tooltipAnchor">{children}</span>
    </Tooltip>
  );
}

const getBudgetAndDates = (flights: InsertionOrderFlight<string>[]) => {
  if (flights.length === 0) {
    return {
      budget: 0,
      dates: {}
    };
  }

  const [allFlightsDates, totalBudget] = flights.reduce(
    (acc: [Moment[], number], flight) => {
      flight.beginDate && acc[0].push(moment.utc(flight.beginDate));
      flight.endDate && acc[0].push(moment.utc(flight.endDate));
      acc[1] += flight.goal.target || 0;
      return acc;
    },
    [[], 0]
  );

  const flightsDates = allFlightsDates.filter(Boolean);
  const minDate = flightsDates.length ? moment.min(flightsDates).format('MMM DD, YYYY') : undefined;
  const maxDate = flightsDates.length ? moment.max(flightsDates).format('MMM DD, YYYY') : undefined;

  return {
    budget: totalBudget,
    dates: {
      end: maxDate,
      start: minDate
    }
  };
};

function SubHeaderContent({
  budget,
  completed,
  dates,
  entity,
  hasUnsavedEntityChanges,
  isSaveDisabled,
  isSnliActive,
  name,
  onExitSnli,
  onExport,
  onHandleButtonClick,
  onProgressTooltipOpenChange,
  parentName,
  saveSteps,
  screenGroupsLayer,
  screens,
  state,
  status,
  type
}: Props) {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);
  const currencyInfo = useGlobalStore(getBaseCurrency);
  const screenDiagnosis =
    entity === 'lineItem' && checkUserResourcePermissions('screenDiagnosis', user.toJSON(), userRoles);

  const [loadingStatus, setLoadingStatus] = useState<'draft-loading' | 'update-loading' | 'idle'>('idle');
  // Which button the progress tooltip belongs to. Unlike `loadingStatus` it
  // survives the save finishing, so the "all saved" / "some changes couldn't
  // be saved" summary stays readable until the next save starts.
  const [progressTooltipFor, setProgressTooltipFor] = useState<'draft' | 'update' | null>(null);

  const handleButtonClick = async (draft = false) => {
    setProgressTooltipFor(draft ? 'draft' : 'update');
    setLoadingStatus(draft ? 'draft-loading' : 'update-loading');
    await onHandleButtonClick(draft);
    setLoadingStatus('idle');
  };

  const tagText: {
    [key in MultiFlightEntityState | 'loading']?: {
      defaultMessage: string;
      id: string;
    };
  } = {
    active: { defaultMessage: 'Active', id: 'subHeader.tagStatus.active' },
    draft: { defaultMessage: `Draft`, id: `subHeader.tagStatus.draft` },
    finished: { defaultMessage: `Finished`, id: `subHeader.tagStatus.finished` },
    launching: { defaultMessage: `Updating`, id: `subHeader.tagStatus.launching` },
    loading: { defaultMessage: `Loading...`, id: `subHeader.tagStatus.loading` },
    paused: { defaultMessage: `Paused`, id: `subHeader.tagStatus.paused` },
    pendingActivation: { defaultMessage: 'Pending Activation', id: `subHeader.tagStatus.pendingActivation` },
    pendingValidation: { defaultMessage: 'Pending Validation', id: 'subHeader.tagStatus.pendingValidation' },
    published: { defaultMessage: 'Not active', id: `subHeader.tagStatus.published` },
    schedulePending: { defaultMessage: 'Pending', id: `subHeader.tagStatus.schedulePending` },
    validationDisapproved: { defaultMessage: 'Validation Disapproved', id: `subHeader.tagStatus.validationDisapproved` }
  };

  const statusTexts = status ? tagText[status as MultiFlightEntityState] : undefined;

  const tagClassName = cn('subHeader__tag', `subHeader__tag--${status || 'draft'}`);

  // A settled save (success or fail) summary is sticky: hovering away and
  // back keeps showing it, it doesn't reset to the preview on its own. It
  // only goes away when the user dismisses it explicitly, or when a new
  // unsaved change appears (the old summary is stale at that point).
  const handleDismiss = () => setProgressTooltipFor(null);

  // Publishing for the first time has a real effect (marks completed, sends to the DSP)
  // even with zero pending edits, so it must stay enabled regardless of unsaved changes.
  // Once completed, the button becomes "Update" and reverts to the no-op-guard behavior.
  const isPublishDisabled = completed && isSaveDisabled;

  const wasSaveDisabledRef = useRef(isSaveDisabled);
  useEffect(() => {
    if (wasSaveDisabledRef.current && !isSaveDisabled && progressTooltipFor !== null) {
      setProgressTooltipFor(null);
    }
    wasSaveDisabledRef.current = isSaveDisabled;
  }, [isSaveDisabled, progressTooltipFor]);

  const isPreview = progressTooltipFor === null;

  const previewSaveSteps: SaveSteps = {
    ...state.pendingSaveSteps,
    entityModel: {
      counts: countAdChanges(state.ads, state.savedBaseline?.ads ?? []),
      status: hasUnsavedEntityChanges ? 'pending' : 'disabled'
    }
  };

  const mainSettingsFields = entity === 'insertionOrder' ? IO_MAIN_SETTINGS_FIELDS : LI_MAIN_SETTINGS_FIELDS;
  const mainSettingsCount = isPreview
    ? countChangedFields(state, state.savedBaseline ?? {}, mainSettingsFields)
    : state.mainSettingsCount;
  const contextTargetingCount = isPreview
    ? countChangedFields(state, state.savedBaseline ?? {}, CONTEXT_TARGETING_FIELDS)
    : state.contextTargetingCount;
  const creativesOtherCount = isPreview
    ? countChangedFields(state, state.savedBaseline ?? {}, CREATIVES_OTHER_FIELDS)
    : state.creativesOtherCount;

  const saveProgressContent = (
    <SaveProgressTooltipContent
      contextTargetingCount={contextTargetingCount}
      creativesOtherCount={creativesOtherCount}
      isDooh={type === 'dooh'}
      isDriveToStore={!!state.attributionSettings?.driveToStore?.enabled}
      isPreview={isPreview}
      mainSettingsCount={mainSettingsCount}
      onDismiss={handleDismiss}
      saveSteps={isPreview ? previewSaveSteps : saveSteps}
    />
  );

  const handleBackButton = () => {
    if (isSnliActive) {
      onExitSnli();
      return;
    }
    navigate(entity === 'insertionOrder' ? '/campaigns?tab=insertionOrder' : '/campaigns?tab=lineItem');
  };

  return (
    <div className="subHeader">
      <aside>
        <ButtonUI
          className="subHeader__backButton"
          icon="fal fa-arrow-left"
          iconPosition="left"
          id="headerIoSettings_backButton"
          onClick={handleBackButton}
          shape="squareRounded"
          size="small"
          type="secondary"
        />
        <div className="subHeader__entityData">
          <span className="subHeader__entityLabel">
            {entity === 'insertionOrder'
              ? `${formatMessage({ defaultMessage: 'IO', id: 'subHeader.ioName' })} - `
              : `${formatMessage({ defaultMessage: 'LI', id: 'subHeader.liName' })} - `}
          </span>
          <div className="subHeader__entityName" title={name}>
            {name || 'N/A'}
          </div>
        </div>
        <div className="subHeader__entityParentData">
          <span className="subHeader__entityParentLabel">
            {entity === 'insertionOrder'
              ? `${formatMessage({ defaultMessage: 'Campaign', id: 'subHeader.campaign' })} - `
              : `${formatMessage({ defaultMessage: 'Insertion Order', id: 'subHeader.insertionOrder' })} - `}
          </span>
          <span className="subHeader__entityParentName" title={parentName}>
            {parentName || 'N/A'}
          </span>
        </div>
      </aside>
      <aside>
        {statusTexts ? (
          <Tag className={tagClassName} id="subHeader-tag-status" status={status === 'active' ? 'ok' : 'highlight'}>
            {formatMessage({ defaultMessage: statusTexts.defaultMessage, id: statusTexts.id })}
          </Tag>
        ) : undefined}
        <div className="subHeader__entityBudgetData">
          <span className="subHeader__entityBudgetLabel">
            {entity === 'insertionOrder'
              ? `${formatMessage({ defaultMessage: 'IO Budget', id: 'subHeader.ioBudget' })} - `
              : `${formatMessage({ defaultMessage: 'LI Budget', id: 'subHeader.liBudget' })} - `}
          </span>
          {budget === 0 ? (
            <span className="subHeader__entityPending">
              {formatMessage({ defaultMessage: 'Pending definition', id: 'subHeader.pending' })}
            </span>
          ) : (
            <span className="subHeader__entityBudgetValue">{normalizeWithUnit(budget, currencyInfo, 2)}</span>
          )}
        </div>
        <div className="subHeader__entityDatesData">
          <span className="subHeader__entityDatesLabel">
            {entity === 'insertionOrder'
              ? `${formatMessage({ defaultMessage: 'IO Dates', id: 'subHeader.ioDates' })} - `
              : `${formatMessage({ defaultMessage: 'LI Dates', id: 'subHeader.liDates' })} - `}
          </span>
          {dates?.start && dates?.end ? (
            <span className="subHeader__entityDatesValue">
              <span className="subHeader__entityDatesDate">{moment.utc(dates.start).format('MMM DD,')}</span>
              <span className="subHeader__entityDatesYear">{moment.utc(dates.start).format('YYYY')}</span>
              <span className="subHeader__entityDatesTo">
                {formatMessage({ defaultMessage: 'to', id: 'subHeader.to' })}
              </span>
              <span className="subHeader__entityDatesDate">{moment.utc(dates.end).format('MMM DD,')}</span>
              <span className="subHeader__entityDatesYear">{moment.utc(dates.end).format('YYYY')}</span>
            </span>
          ) : (
            <span className="subHeader__entityPending">
              {formatMessage({ defaultMessage: 'Pending definition', id: 'subHeader.pending' })}
            </span>
          )}
        </div>
        <div className="subHeader__actions">
          <SaveProgressTooltip
            content={saveProgressContent}
            enabled={!isPublishDisabled || progressTooltipFor === 'update'}
            onOpenChange={onProgressTooltipOpenChange}
          >
            <ButtonUI
              className={cn('subHeader__updateButton', {
                'subHeader__updateButton--disabled': isPublishDisabled
              })}
              disabled={isPublishDisabled}
              key="headerButtons-add"
              loading={loadingStatus === 'update-loading'}
              onClick={() => handleButtonClick()}
              size="small"
              text={formatMessage({
                defaultMessage: `${state.completed ? 'Update' : 'Publish'}`,
                id: `subHeader.${state.completed ? 'updateButton' : 'publishButton'}`
              })}
              type="primary"
              visible
            />
          </SaveProgressTooltip>
          {!completed && (
            <SaveProgressTooltip
              content={saveProgressContent}
              enabled={!isSaveDisabled || progressTooltipFor === 'draft'}
              onOpenChange={onProgressTooltipOpenChange}
            >
              <ButtonUI
                className="subHeader__draftButton"
                disabled={isSaveDisabled}
                icon="fa-light fa-floppy-disk"
                iconPosition="left"
                id="subHeader-draftButton"
                key="headerButtons-save"
                loading={loadingStatus === 'draft-loading'}
                onClick={() => handleButtonClick(true)}
                size="small"
                type="secondary"
                visible
              />
            </SaveProgressTooltip>
          )}
          {type === 'dooh' && (
            <ContextualDOOHHeaderMenu
              buttonProps={{ size: 'small', type: 'tertiary' }}
              entity={state}
              exportConfig={{ onExport, screenGroupsLayer }}
              id="contextualDOOHHeaderMenu"
              screens={screens}
              showScreenDiagnosis={screenDiagnosis}
            />
          )}
        </div>
      </aside>
    </div>
  );
}

type UseSubHeaderLogicProps = {
  actions: {
    edit: () => Promise<{ _id?: string } | void>;
    save: () => Promise<{ _id?: string } | void>;
  };
  customPreValidation?: (draft: boolean) => Promise<IoSettingValidationError[] | void>;
  entityType: 'insertionOrder' | 'lineItem';
  fetchParent: (userId: string) => Promise<any>;
  fetchStatus: (userId: string, id: string) => Promise<any>;
  formContext: any;
  isProgressTooltipOpen: boolean;
  parentId: string | undefined;
  state: any;
};

function useSubHeaderLogic({
  actions,
  customPreValidation,
  entityType,
  fetchParent,
  fetchStatus,
  formContext,
  isProgressTooltipOpen,
  parentId,
  state
}: UseSubHeaderLogicProps) {
  const { user: userId = '' } = useGlobalStore(store => getSessionByKeys(store, ['user']));
  const navigate = useNavigate();
  const { allowNextNavigation } = useNavigationGuard(false);
  const { formatMessage } = useIntl();
  const { _id, completed, pendingToCreate, type, setCompleted } = state;
  const { addScreenInventoryExportId, getScreens, getZones, screenGroupsLayer, validateForm } = formContext;

  const { data: parentData } = useQueryFetch(
    [`${entityType}InfoBar_parent`, userId, parentId],
    () => fetchParent(userId),
    { enabled: !!parentId, staleTime: 0 }
  );

  const { data: statusData } = useQueryFetch([`get${entityType}State`, userId, _id], () => fetchStatus(userId, _id!), {
    enabled: !!_id,
    // @ts-ignore
    initialData: { state: _id ? 'loading' : 'draft' },
    staleTime: 0
  });

  // Out of sync with the bidder shows as "Updating" on top of its lifecycle status
  const status = statusData?.updating ? 'launching' : statusData?.state;

  // The save response does not say whether the bidder sync failed: an IO saved while Updating did not get there
  const isBidderSyncPending = async (saved?: { _id?: string } | void) => {
    if (entityType !== 'insertionOrder' || !saved?._id) {
      return false;
    }
    // A failed read must not turn a saved entity into a failed save
    const syncState = await fetchStatus(userId, saved._id).catch(() => undefined);
    return Boolean(syncState?.updating);
  };

  const runCommonValidations = (): IoSettingValidationError[] => {
    if (entityType === 'insertionOrder') {
      return [];
    }
    const mapItems = type === 'dooh' ? getScreens() : getZones();
    if (checkLocationsOrScreensAreConfigured(mapItems)) {
      return [];
    }
    return [{ error: 'locations' }];
  };

  const notifySyncError = (message: MessageDescriptor) => {
    showNotification(
      'warning',
      formatMessage(message, { entity: entityType === 'insertionOrder' ? 'Insertion Order' : 'Line Item' })
    );
  };

  const handleSaveDraft = async () => {
    const formErrors = Object.values(validateForm(true) || {});
    taptap.entity = {
      ...taptap.entity,
      validationErrors: {
        formErrors
      }
    };

    if (formErrors.length > 0) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'There are validation errors in the configuration. Please review the highlighted sections.',
          id: 'ioSettings.errors.validationErrorsMain'
        })
      );
      return;
    }

    try {
      if (pendingToCreate) {
        await actions.save();
        if (entityType === 'insertionOrder') {
          window.history.replaceState(null, '', `/ioSettings/edit/${_id}`);
        }
      } else {
        await actions.edit();
      }
      showNotification(
        'success',
        formatMessage({
          defaultMessage: `${entityType === 'insertionOrder' ? 'Insertion Order' : 'Line Item'} edited successfully`,
          id: `MTFSettings.header.${entityType}.updateSuccess`
        })
      );
    } catch (_e) {
      // The save-progress tooltip already tells the user a step failed while
      // it's open — a toast saying the same thing on top of it is redundant.
      if (!isProgressTooltipOpen) {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: `Failure editing the ${entityType === 'insertionOrder' ? 'Insertion Order' : 'Line Item'}`,
            id: `MTFSettings.header.${entityType}.updateFailure`
          })
        );
      }
    }
  };

  const handleSavePublish = async () => {
    const formErrors = Object.values(validateForm(false) || {});

    const commonErrors = runCommonValidations();

    taptap.entity = {
      ...taptap.entity,
      validationErrors: {
        commonErrors,
        formErrors
      }
    };

    let specificErrors: IoSettingValidationError[] = [];
    try {
      if (customPreValidation) {
        const result = await customPreValidation(false);
        if (result && Array.isArray(result)) {
          specificErrors = result;
        }
      }
    } catch (_e) {
      return;
    }

    const allErrors = [...specificErrors, ...commonErrors];

    if (allErrors.length > 0 || formErrors.length > 0) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'There are validation errors in the configuration. Please review the highlighted sections.',
          id: 'ioSettings.errors.validationErrorsMain'
        })
      );
      return;
    }

    setCompleted(true);
    try {
      const successMsgId = completed
        ? `MTFSettings.header.${entityType}.updateSuccess`
        : `MTFSettings.header.${entityType}.createSuccess`;
      if (pendingToCreate) {
        const saved = await actions.save();
        if (await isBidderSyncPending(saved)) {
          notifySyncError(BIDDER_SYNC_ERROR_MESSAGE);
        } else {
          showNotification('success', formatMessage({ defaultMessage: 'Saved successfully', id: successMsgId }));
        }
        allowNextNavigation();
        navigate(`/campaigns?tab=${entityType}`);
      } else {
        const saved = await actions.edit();
        const syncPending = await isBidderSyncPending(saved);
        if (syncPending) {
          notifySyncError(BIDDER_SYNC_ERROR_MESSAGE);
        }
        if (!completed) {
          allowNextNavigation();
          navigate(`/campaigns?tab=${entityType}`);
          if (!syncPending) {
            showNotification('success', formatMessage({ defaultMessage: 'Saved successfully', id: successMsgId }));
          }
        }
      }
    } catch (e) {
      const syncErrorMessage = getSavedEntitySyncErrorMessage(e);
      if (syncErrorMessage) {
        // The entity is stored, so keep the success navigation instead of trapping the user in the form.
        notifySyncError(syncErrorMessage);
        if (!completed) {
          allowNextNavigation();
          navigate(`/campaigns?tab=${entityType}`);
        }
        return;
      }
      const failMsgId = completed
        ? `MTFSettings.header.${entityType}.updateFailure`
        : `MTFSettings.header.${entityType}.createFailure`;
      // The save-progress tooltip already tells the user a step failed while
      // it's open — a toast saying the same thing on top of it is redundant.
      if (!isProgressTooltipOpen) {
        showNotification('error', formatMessage({ defaultMessage: 'Failure saving', id: failMsgId }));
      }
    }
  };

  const handleButtonClick = async (draft = false) => {
    if (draft) {
      await handleSaveDraft();
      return;
    }
    await handleSavePublish();
  };

  const handleExport = (response: Record<string, any>) => {
    addScreenInventoryExportId(response._id);
  };

  return {
    handleButtonClick,
    handleExport,
    parentData,
    screenGroupsLayer,
    screens: getScreens() || [],
    status
  };
}

function SubHeaderIO() {
  const state = useInsertionOrderStore() as IoSettingsStore;
  const formContext = useInsertionOrderFormContext();
  const { isSnliActive, setIsSnliActive } = formContext;
  const { user: userId = '' } = useGlobalStore(store => getSessionByKeys(store, ['user']));
  const currencyInfo = useGlobalStore(getBaseCurrency);
  const { formatMessage } = useIntl();
  const [isProgressTooltipOpen, setIsProgressTooltipOpen] = useState(false);

  const customPreValidation = async () => {
    const errors = await isValidIoSettings(state, currencyInfo, userId, {});
    const flightBudgetError = errors.find(({ error }) => error === 'flightBudget');
    if (flightBudgetError) {
      showNotification(
        'error',
        formatMessage(
          {
            defaultMessage:
              'The budget of flight {flightName} cannot be lower than its current spend ({flightCurrentSpend})',
            id: 'MTFSettings.header.insertionOrder.flightBudgetLowerThanSpent'
          },
          {
            flightCurrentSpend: String(flightBudgetError.extraData?.flightCurrentSpend ?? ''),
            flightName: String(flightBudgetError.extraData?.flightName ?? '')
          }
        )
      );
      throw new Error('The budget of a flight cannot be lower than its current spend.');
    }
    return errors;
  };

  const logic = useSubHeaderLogic({
    actions: {
      edit: state.editIoSettings,
      save: state.saveIoSettings
    },
    customPreValidation,
    entityType: 'insertionOrder',
    fetchParent: userId => getCampaignPlan(userId, state.campaignPlan, {}),
    fetchStatus: async (userId, id) => {
      const response = await getInsertionOrderState(userId, id);
      return response;
    },
    formContext,
    isProgressTooltipOpen,
    parentId: state.campaignPlan,
    state
  });

  const { budget, dates } = getBudgetAndDates(state.flights);

  return (
    <SubHeaderContent
      budget={budget}
      completed={state.completed}
      dates={dates}
      entity="insertionOrder"
      hasUnsavedEntityChanges={getHasUnsavedIOEntityChanges(state) || getHasUnsavedIODoohAudienceChanges(state)}
      isSaveDisabled={!state.pendingToCreate && !getHasUnsavedIOChanges(state)}
      isSnliActive={isSnliActive}
      name={state.name || ''}
      onExitSnli={() => setIsSnliActive(false)}
      onExport={logic.handleExport}
      onHandleButtonClick={logic.handleButtonClick}
      onProgressTooltipOpenChange={setIsProgressTooltipOpen}
      parentName={logic.parentData?.name || ''}
      saveSteps={state.saveSteps}
      screenGroupsLayer={logic.screenGroupsLayer}
      screens={logic.screens}
      state={state}
      status={logic.status}
      type={state.type}
    />
  );
}

function SubHeaderLI() {
  const state = useLineItemStore() as LineItemStore;
  const formContext = useLineItemFormContext();
  const { isSnliActive, setIsSnliActive } = formContext;
  const { user: userId = '' } = useGlobalStore(store => getSessionByKeys(store, ['user']));
  const { formatMessage } = useIntl();
  const [isProgressTooltipOpen, setIsProgressTooltipOpen] = useState(false);
  const overallBudgetGoal =
    state.goals?.find(({ type, timeFrame }) => type === GoalTypeEnum.budget && timeFrame === GoalTimeFrameEnum.overall)
      ?.target || 0;
  const customPreValidation = async () => {
    if (!state.completed) {
      return;
    }
    const pacingData: LineItemPacingProgressTotal | undefined = await getLineItemPacing(userId, state._id).catch(
      () => undefined
    );
    const overallBudgetPacing = pacingData?.globalGoals?.find(
      goal => goal.type === GoalTypeEnum.budget && goal.timeFrame === GoalTimeFrameEnum.overall
    );
    const currentPacing = overallBudgetPacing?.current;
    const hasOverallBudgetGoal = state.goals?.some(
      ({ type, timeFrame }) => type === GoalTypeEnum.budget && timeFrame === GoalTimeFrameEnum.overall
    );

    if (!currentPacing || !hasOverallBudgetGoal) {
      return;
    }

    if (currentPacing > overallBudgetGoal) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'The new budget cannot be lower than the current consumed amount',
          id: 'MTFSettings.header.lineItem.newBudgetLowerThanConsumed'
        })
      );
      throw new Error('The new budget cannot be lower than the current consumed amount.');
    }
  };

  const logic = useSubHeaderLogic({
    actions: {
      edit: state.editLineItem,
      save: state.saveLineItem
    },
    customPreValidation,
    entityType: 'lineItem',
    fetchParent: async userId => {
      const ent = await getInsertionOrder(userId, state.insertionOrder!, {});
      return ent.toJSON();
    },
    fetchStatus: async (_userId, id) => {
      return await getLineItemStatus(id);
    },
    formContext,
    isProgressTooltipOpen,
    parentId: state.insertionOrder,
    state
  });

  const { beginDate, endDate } = state;
  const dates =
    beginDate && endDate
      ? {
          end: moment.utc(endDate).format('MMM DD, YYYY'),
          start: moment.utc(beginDate).format('MMM DD, YYYY')
        }
      : {};

  return (
    <SubHeaderContent
      budget={overallBudgetGoal}
      completed={state.completed}
      dates={dates}
      entity="lineItem"
      hasUnsavedEntityChanges={getHasUnsavedLIEntityChanges(state) || getHasUnsavedLIDoohAudienceChanges(state)}
      isSaveDisabled={!state.pendingToCreate && !getHasUnsavedLIChanges(state)}
      isSnliActive={isSnliActive}
      name={state.name || ''}
      onExitSnli={() => setIsSnliActive(false)}
      onExport={logic.handleExport}
      onHandleButtonClick={logic.handleButtonClick}
      onProgressTooltipOpenChange={setIsProgressTooltipOpen}
      parentName={logic.parentData?.name || ''}
      saveSteps={state.saveSteps}
      screenGroupsLayer={logic.screenGroupsLayer}
      screens={logic.screens}
      state={state}
      status={logic.status}
      type={state.type}
    />
  );
}

export function SubHeader({ entity }: { entity: MultiFlightEntityType }) {
  return entity === 'insertionOrder' ? <SubHeaderIO /> : <SubHeaderLI />;
}
